import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ToolEnum } from '../../models/tool.enum';
import { ToolIcons } from '../../models/tool-icons.const';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { EngineState } from '../../state/engine.state';
import { SetActiveTool, ToggleLabels, PlayAnimation } from '../../state/engine.actions';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { Undo, Redo, RevertChapterSynchronization, CopyChapterSynchronization } from 'src/app/history/state/history.actions';
import { PaintPresets, PaintPresetFade, PaintPresetOriginal } from 'src/app/engine/models/paint-presets.const';
import { EngineNodesState } from '../../state/nodes/engine.nodes.state';
import { PaintService } from '../../paint.service';
import * as _ from 'lodash';
import { HistoryState } from 'src/app/history/state/history.state';
import { SetChapter } from 'src/app/story/state/chapters/story.chapters.actions';
import { ChapterService } from 'src/app/story/chapter.service';
import { ViewTreeNode } from '../../models/view.tree-node';
import { takeUntil } from 'rxjs/operators';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { SetObjectVisibility } from 'src/app/edit/state/edit.actions';
import { Router } from '@angular/router';
import { ngxZendeskWebwidgetService } from 'ngx-zendesk-webwidget';

@Component({
  selector: 'app-engine-tool-bar',
  templateUrl: './engine-tool-bar.component.html',
  styleUrls: ['./engine-tool-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EngineToolBarComponent implements OnInit, OnDestroy {
  toolEnum = ToolEnum;
  toolIcons = ToolIcons;
  paintPresets = PaintPresets;

  @Select(EngineState.getShowLabels)
  showLabels$: Observable<boolean>;

  @Select(EngineState.getActiveTool)
  activeTool$: Observable<ToolEnum>;

  @Select(EngineState.getAnimationPlaying)
  animationPlaying$: Observable<boolean>;

  @Select(StoryChaptersState.getSelectedChapter)
  selectedChapter$: Observable<ChapterModel>;

  @Select(EngineNodesState.getCheckedNodes)
  checkedNodes$: Observable<ViewTreeNode[]>;

  leaves: string[] = [];
  count = 0;

  private ngUnsubscribe = new Subject();

  constructor(
    private store: Store,
    private paintService: PaintService,
    private chapterService: ChapterService,
    private graphService: BdGraphService,
    private changeDetector: ChangeDetectorRef,
    private router: Router,
    private zendeskService: ngxZendeskWebwidgetService
  ) { }

  ngOnInit() {
    this.checkedNodes$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((nodes) => {
        this.leaves = this.getLeaves(nodes);
        this.count = this.leaves.length;
        this.changeDetector.markForCheck();
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  

  handleUndoClick() {
    this.undo();
  }

  handleRedoClick() {
    this.redo();
  }

  handleResetClick() {
    this.reset();
  }

  handleShowSelected() {
    this.store.dispatch(new SetObjectVisibility(this.store.selectSnapshot(EngineNodesState.getCheckedNodes), true));
  }

  handleShowInverse() {
    const nodes = this.getInverseSelectedNodes();
    this.store.dispatch(new SetObjectVisibility(nodes, true));
  }

  handleHideSelected() {
    this.store.dispatch(new SetObjectVisibility(this.store.selectSnapshot(EngineNodesState.getCheckedNodes), false));
  }

  handleHideInverse() {
    const nodes = this.getInverseSelectedNodes();
    this.store.dispatch(new SetObjectVisibility(nodes, false));
  }

  handleFadeSelected(inverse = false) {
    const nodes = this.store.selectSnapshot(EngineNodesState.getCheckedNodes);
    const inverseNodes = this.getInverseSelectedNodes();    
    
    if (inverse) {
      this.paintService.paintAll(nodes, PaintPresetOriginal.recipe);
      this.paintService.paintAll(inverseNodes, PaintPresetFade.recipe);
    } else {
      this.paintService.paintAll(nodes, PaintPresetFade.recipe);
      this.paintService.paintAll(inverseNodes, PaintPresetOriginal.recipe);
    }
  }

  handleNavigate(url) {
    this.router.navigateByUrl(url);
  }


  handleHelpClicked() {
    this.showZendeskWidget();
  }

  showZendeskWidget() {
    this.zendeskService.activate({
      hideOnClose: true
    });
  }

  getInverseSelectedNodes() {
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    const nodeIds = this.store.selectSnapshot(EngineNodesState.getCheckedNodes).map(node => node.id);
    const inverseNodeIds = this.graphService.getInverseSelection(viewGraph, nodeIds);
    return this.graphService.getNodes(viewGraph, inverseNodeIds);
  }

  getLeaves(nodes: ViewTreeNode[]): string[] {
    if (!nodes) {
      return [];
    }

    return this.graphService.getAllLeafDescendants(
      this.store.selectSnapshot(EngineState.getViewGraph), 
      nodes.map(node => node.id)
    );
  }

  toggleTool(tool: ToolEnum) {
    this.store.dispatch(new SetActiveTool(tool));
  }

  toggleLabels() {
    const showLabels = !this.store.selectSnapshot(EngineState.getShowLabels);
    this.store.dispatch(new ToggleLabels(showLabels));
  }

  play() {
    const playing = !this.store.selectSnapshot(EngineState.getAnimationPlaying);
    this.store.dispatch(new PlayAnimation(playing));
  }

  undo() {
    this.store.dispatch(new Undo());
  }

  redo() {
    this.store.dispatch(new Redo());
  }

  reset() {
    let chapter = this.store.selectSnapshot(StoryChaptersState.getSelectedChapter);
    this.store.dispatch(new RevertChapterSynchronization('root', chapter.id));
    this.store.dispatch(new CopyChapterSynchronization('root', chapter.id, 'cache', chapter.id));

    const cache = this.store.selectSnapshot(HistoryState).cache;
    const synchronization = cache[`root_${chapter.id}`];

    chapter = {
      ...chapter,
      synchronization: {
        ...synchronization
      }
    };

    this.store.dispatch(new SetChapter(chapter));
    this.chapterService.addChapterToEngine(chapter, 0);
  }
}
